import React, { useState } from "react";
import { ExternalLink, PlayCircle } from "lucide-react";

interface YouTubeFacadeProps {
  videoId: string;
  title: string;
  /**
   * Marks a development-only placeholder video. Placeholder facades render
   * in development so the pipeline can be reviewed, but must never render
   * on a production build, since no real lesson recording exists yet.
   */
  isIllustrative?: boolean;
}

const YouTubeFacade: React.FC<YouTubeFacadeProps> = ({ videoId, title, isIllustrative }) => {
  const [loaded, setLoaded] = useState(false);

  if (isIllustrative && process.env.NODE_ENV === "production") {
    return (
      <div className="lesson-video lesson-video--pending" role="status">
        <p>Video lesson recording is in progress. The written lesson above is already complete.</p>
      </div>
    );
  }

  return (
    <figure className="lesson-video">
      {isIllustrative && (
        <p className="lesson-video__illustrative-tag">Illustrative preview — not the final lesson recording</p>
      )}
      {loaded ? (
        <div className="lesson-video__frame">
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <button
          type="button"
          className="lesson-video__facade"
          onClick={() => setLoaded(true)}
          aria-label={`Play video: ${title}`}
        >
          <img
            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
            alt=""
            aria-hidden="true"
            loading="lazy"
          />
          <span className="lesson-video__play"><PlayCircle aria-hidden="true" size={56} /></span>
          <span className="lesson-video__title">{title}</span>
        </button>
      )}
      <a
        className="lesson-video__fallback"
        href={`https://www.youtube.com/watch?v=${videoId}`}
        target="_blank"
        rel="noreferrer"
      >
        Watch on YouTube <ExternalLink aria-hidden="true" size={15} />
      </a>
    </figure>
  );
};

export default YouTubeFacade;
